import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  FormControl,
  FormLabel,
  Input,
  Spinner,
  Table,
  Tag,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Link as ChLink,
  Text,
  HStack,
  Center,
  Divider,
  VStack,
} from "@chakra-ui/react";
import { Select } from "chakra-react-select";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AdminApiClient } from "../../api/adminApiClient";
import { ErrorMessage } from "../../components/ErrorMessage/ErrorMessage";
import { chakraStyles } from "../../components/fields/SelectField/ChakraReactSelectField";
import { SelectPeriod } from "../../components/fields/SelectPeriod/SelectPeriod";
import { PageContent } from "../../components/PageContent/PageContent";
import { PageHeading } from "../../components/PageHeading/PageHeading";
import { ReportType } from "../../enums/ReportType";
import { useAdminApiClient } from "../../hooks/useApiClient";
import { useAdminApiRequest } from "../../hooks/useApiRequest";

interface ActionButtonProps {
  apiClient: AdminApiClient;
  reportType: ReportType;
  startDate?: string | null;
  endDate?: string | null;
  businessIds: string[];
}

const ActionButton = ({
  startDate,
  endDate,
  apiClient,
  businessIds,
  reportType,
}: ActionButtonProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [result, setResult] = useState<string>();
  const [isNoData, setIsNoData] = useState(false);
  useEffect(() => {
    setIsLoading(false);
    setErrorMessage(undefined);
    setResult(undefined);
    setIsNoData(false);
  }, [startDate, endDate, businessIds]);

  const generateReport = async () => {
    setIsLoading(true);
    const result = await apiClient.generateNonComparativeReport(
      reportType,
      startDate,
      endDate,
      businessIds
    );
    if (result.ok) {
      setResult(result.data);
    } else {
      if (result.statusCode === 404) {
        setIsNoData(true);
      } else {
        setErrorMessage(result.message);
      }
    }
    setIsLoading(false);
  };
  const businessId = businessIds ? `-${businessIds.join("-")}` : "";
  return (
    <>
      {isLoading && <Spinner size="xs" />}
      {!isLoading && !errorMessage && !result && !isNoData && (
        <Button size="xs" onClick={generateReport}>
          Generate
        </Button>
      )}
      {!isLoading && !errorMessage && result && !isNoData && (
        <ChLink
          href={result}
          download={`${reportType}-${startDate ?? "no-start-date"}-${
            endDate ?? "no-end-date"
          }${businessId}.csv`}
        >
          Download
        </ChLink>
      )}
      {!isLoading && isNoData && <Tag>No Data</Tag>}
      {!isLoading && errorMessage && (
        <ErrorMessage>{errorMessage}</ErrorMessage>
      )}
    </>
  );
};

export const StandardReport = () => {
  const [startDate, setStartDate] = useState<string | undefined>("");
  const [endDate, setEndDate] = useState<string | undefined>("");
  const [selectedBusinesses, setSelectedBusinesses] = useState<string[]>([]);

  const apiClient = useAdminApiClient();
  const onPeriodChange = (
    startPeriodDate: string | undefined,
    endPeriodDate: string | undefined
  ) => {
    setStartDate(startPeriodDate);
    setEndDate(endPeriodDate);
  };

  const {
    data: businesses,
    error: businessError,
    isLoading: businessLoading,
  } = useAdminApiRequest((client) => client.getBusinesses(""), []);

  const onSelectBusiness = (e: readonly { label: string; value: string }[]) => {
    setSelectedBusinesses(e.map((item) => item.value));
  };

  return (
    <>
      <PageHeading>
        <PageHeading.Title>Standard Reports</PageHeading.Title>
      </PageHeading>
      <PageContent>
        <VStack w="100%" alignItems="flex-start" pb={4}>
          <Breadcrumb variant="pageHeading">
            <BreadcrumbItem>
              <BreadcrumbLink as={Link} to="/admin/reports">
                Reports
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem>
              <BreadcrumbLink as={Link} to="/admin/reports/standard">
                Standard Reports
              </BreadcrumbLink>
            </BreadcrumbItem>
          </Breadcrumb>

          <Text as="h2" fontSize="lg" fontWeight="bold" color="cherryUi.600">
            Reporting Period:
          </Text>

          <HStack spacing={6}>
            <Box py={4}>
              <FormControl>
                <FormLabel>By Period:</FormLabel>
                <SelectPeriod onChange={onPeriodChange} />
              </FormControl>
            </Box>
            <Center h="80px">
              <Divider orientation="vertical" variant="horizontal-divider" />
            </Center>
            <Box py={4}>
              <FormControl>
                <FormLabel>Start date</FormLabel>
                <Input
                  type="date"
                  placeholder="Start date"
                  onChange={(e) => setStartDate(e.target.value)}
                  value={startDate}
                />
              </FormControl>
            </Box>
            <Box py={4}>
              <FormControl>
                <FormLabel>End date</FormLabel>
                <Input
                  type="date"
                  placeholder="End date"
                  onChange={(e) => setEndDate(e.target.value)}
                  value={endDate}
                />
              </FormControl>
            </Box>
          </HStack>

          <FormControl maxW="lg">
            <FormLabel fontSize="lg" fontWeight="bold" color="cherryUi.600">
              Filter by Business
            </FormLabel>
            <Select
              isMulti
              options={
                businesses?.Results.map((item) => {
                  return {
                    label: item.DisplayName!,
                    value: item.Meta!.Id!,
                  };
                }) || []
              }
              chakraStyles={chakraStyles}
              isLoading={businessLoading}
              placeholder="Select businesses"
              onChange={onSelectBusiness}
            />
          </FormControl>
        </VStack>

        <Box w="100%">
          <Table w="100%" size="sm">
            <Thead>
              <Tr>
                <Th w="75%">Report Type</Th>
                <Th>Action</Th>
              </Tr>
            </Thead>
            <Tbody>
              <Tr>
                <Td w="75%">Cherry Pay Card Status</Td>
                <Td>
                  <ActionButton
                    apiClient={apiClient}
                    reportType={ReportType.CherryPay_CardStatus}
                    startDate={startDate}
                    endDate={endDate}
                    businessIds={selectedBusinesses}
                  />
                </Td>
              </Tr>
              <Tr>
                <Td w="75%">Cherry Pay Card Program Type</Td>
                <Td>
                  <ActionButton
                    apiClient={apiClient}
                    reportType={ReportType.CherryPay_CardProgramType}
                    startDate={startDate}
                    endDate={endDate}
                    businessIds={selectedBusinesses}
                  />
                </Td>
              </Tr>
              <Tr>
                <Td w="75%">Cherry Pay Card Funds Loaded</Td>
                <Td>
                  <ActionButton
                    apiClient={apiClient}
                    reportType={ReportType.CherryPay_CardFundsLoaded}
                    startDate={startDate}
                    endDate={endDate}
                    businessIds={selectedBusinesses}
                  />
                </Td>
              </Tr>
              <Tr>
                <Td w="75%">Cherry Pass Downloaded</Td>
                <Td>
                  <ActionButton
                    apiClient={apiClient}
                    reportType={ReportType.CherryPass_Downloaded}
                    startDate={startDate}
                    endDate={endDate}
                    businessIds={selectedBusinesses}
                  />
                </Td>
              </Tr>
              <Tr>
                <Td w="75%">Cherry Play Promotion Leaderboard Players</Td>
                <Td>
                  <ActionButton
                    apiClient={apiClient}
                    reportType={
                      ReportType.CherryPlay_PromotionLeaderboardPlayers
                    }
                    startDate={startDate}
                    endDate={endDate}
                    businessIds={selectedBusinesses}
                  />
                </Td>
              </Tr>
              <Tr>
                <Td w="75%">Cherry Play Promotion Leaderboard</Td>
                <Td>
                  <ActionButton
                    apiClient={apiClient}
                    reportType={ReportType.CherryPlay_PromotionLeaderboard}
                    startDate={startDate}
                    endDate={endDate}
                    businessIds={selectedBusinesses}
                  />
                </Td>
              </Tr>
              <Tr>
                <Td w="75%">Cherry Play Promotion Reward Detail</Td>
                <Td>
                  <ActionButton
                    apiClient={apiClient}
                    reportType={ReportType.CherryPlay_PromotionRewardDetail}
                    startDate={startDate}
                    endDate={endDate}
                    businessIds={selectedBusinesses}
                  />
                </Td>
              </Tr>
              <Tr>
                <Td w="75%">Cherry Play Promotion Coupon Summary</Td>
                <Td>
                  <ActionButton
                    apiClient={apiClient}
                    reportType={ReportType.CherryPlay_PromotionCouponSummary}
                    startDate={startDate}
                    endDate={endDate}
                    businessIds={selectedBusinesses}
                  />
                </Td>
              </Tr>
            </Tbody>
          </Table>
        </Box>
      </PageContent>
    </>
  );
};
